"use client";

import { createContactUsLocalisedSchema, ContactUsSchema } from "@/lib/validation/contact-us";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { cn, getHomePageSlug } from "@/lib/utils";
import Button, { buttonVariants, geistSans } from "@/components/atoms/buttons/Button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import Link from "next/link";
import RadioButton from "@/components/atoms/radio-buttons";
import ContactDetails from "@/components/organisms/forms/contact-details";
import { Graphics } from "@/components/graphics";
import OptIn from "@/components/atoms/opt-in";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { sdk } from "@/lib/medusa/client";
import { ContactDocumentData } from "@/prismicio-types";
import { useEffect, useRef, useState, useTransition } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "@/components/atoms/toast";
import TurnstileWidget from "@/components/atoms/turnstile-widget";

type ContactUsFormProps = {
  locale: Locales;
  data: ContactDocumentData;
};

const ContactUsForm = ({ locale, data }: ContactUsFormProps) => {
  const { contactUsSchema } = createContactUsLocalisedSchema(locale);
  const successMessageRef = useRef<HTMLDivElement>(null);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [isPending, startTransition] = useTransition();

  const form = useForm<ContactUsSchema>({
    resolver: zodResolver(contactUsSchema),
    defaultValues: {
      title: undefined,
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      message: "",
      receiveNews: false,
      cgu: false,
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  useEffect(() => {
    if (!isSuccessful) return;

    const frame = window.requestAnimationFrame(() => {
      successMessageRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isSuccessful]);

  const onSubmit = (data: ContactUsSchema) => {
    if (!turnstileToken) {
      toast.error({ title: __("Please complete the security check before sending your message.", locale) });
      return;
    }

    startTransition(async () => {
      try {
        await sdk.client.fetch("store/contact-us", {
          method: "POST",
          body: {
            language: locale,
            title: data.title,
            first_name: data.firstName,
            last_name: data.lastName,
            email: data.email,
            phone: data.phone,
            message: data.message,
            subscribed_to_news: data.receiveNews,
            cgu: data.cgu,
            lang: locale,
            turnstile_token: turnstileToken,
          },
        });

        toast.success({ title: __("Your message has been sent successfully!", locale) });
        setIsSuccessful(true);
      } catch (error) {
        if (error instanceof SyntaxError && error.message.includes("Unexpected end of JSON input")) {
          toast.success({ title: __("Your message has been sent successfully!", locale) });
          setIsSuccessful(true);
          return;
        }

        // Handle actual errors
        setIsSuccessful(false);
        setTurnstileToken(null);
        setTurnstileResetKey((key) => key + 1);
        console.error("Error submitting contact form:", error);
        toast.error({ title: __("An error occurred while submitting the form. Please try again later.", locale) });
      }
    });
  };

  return (
    <div className={cn("text-dark-primary mx-auto max-w-[728px] space-y-5", geistSans.className)}>
      {!isSuccessful ? (
        <>
          <Form {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <FormField
                control={control}
                name="title"
                render={({ field }) => (
                  <FormItem className="w-full gap-1">
                    <div className="flex flex-col gap-2.5">
                      <FormLabel className="mb-0 text-base font-medium">
                        {__("Civility", locale)} <span className="text-pink-primary">*</span>
                      </FormLabel>
                      <RadioButton
                        disabled={isPending}
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        options={[
                          { value: "mademoiselle", label: __("Miss ", locale) },
                          { value: "madame", label: __("Mrs ", locale) },
                          { value: "monsieur", label: __("Mr", locale) },
                        ]}
                      />
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex items-center gap-3">
                <FormField
                  control={control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem className={cn("w-full gap-1", errors.lastName && !errors.firstName && "mb-5")}>
                      <div className="flex flex-col gap-2.5">
                        <FormLabel className="mb-0 text-base font-medium">
                          {__("First name", locale)} <span className="text-pink-primary">*</span>
                        </FormLabel>

                        <FormControl>
                          <Input
                            disabled={isPending}
                            className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] bg-white placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                            {...field}
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem className={cn("w-full gap-1", errors.firstName && !errors.lastName && "mb-5")}>
                      <div className="flex flex-col gap-2.5">
                        <FormLabel className="mb-0 text-base font-medium">
                          {__("Last name", locale)} <span className="text-pink-primary">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            disabled={isPending}
                            className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] bg-white placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                            {...field}
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex flex-col items-center gap-6 sm:flex-row sm:gap-3">
                <FormField
                  control={control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className={cn("w-full gap-1", errors.phone && !errors.email && "mb-5")}>
                      <div className="flex flex-col gap-2.5">
                        <FormLabel className="mb-0 text-base font-medium">
                          {__("Email Address", locale)} <span className="text-pink-primary">*</span>
                        </FormLabel>

                        <FormControl>
                          <Input
                            type="email"
                            disabled={isPending}
                            className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] bg-white placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                            {...field}
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem className={cn("w-full gap-1", errors.email && !errors.phone && "mb-5")}>
                      <div className="flex flex-col gap-2.5">
                        <FormLabel className="mb-0 text-base font-medium">
                          {__("Phone", locale)} <span className="text-pink-primary">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            disabled={isPending}
                            className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] bg-white placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                            {...field}
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={control}
                name="message"
                render={({ field }) => (
                  <FormItem className="w-full gap-1">
                    <div className="flex flex-col gap-2.5">
                      <FormLabel className="mb-0 text-base font-medium">{__("Your message", locale)}</FormLabel>
                      <FormControl>
                        <Textarea
                          disabled={isPending}
                          className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light min-h-[157px] resize-none bg-white placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* Temporary hide newsletter field */}
              {/* <FormField
                control={control}
                name="receiveNews"
                render={({ field }) => (
                  <FormItem>
                    <OptIn
                      disabled={isPending}
                      id="receive-news"
                      checked={field.value}
                      label={__("I want to receive Festi news", locale)}
                      onCheckedChange={field.onChange}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              /> */}
              <FormField
                control={control}
                name="cgu"
                render={({ field }) => (
                  <FormItem>
                    <OptIn
                      disabled={isPending}
                      id="cgu"
                      checked={field.value}
                      label={__("I accept the", locale)}
                      onCheckedChange={field.onChange}
                      link={data.cgu_link}
                      linkText={__("terms and conditions", locale)}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <TurnstileWidget
                locale={locale}
                siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
                onTokenChange={setTurnstileToken}
                resetKey={turnstileResetKey}
              />
              <Button
                disabled={isPending || !turnstileToken}
                variant="rounded"
                type="submit"
                className="h-11 w-full cursor-pointer font-semibold"
                size="md"
              >
                {isPending && <Loader2 className="mr-2 animate-spin" />} {__("Contact us", locale)}
              </Button>
            </form>
          </Form>
          <ContactDetails data={data} locale={locale} />
        </>
      ) : (
        <>
          <div
            ref={successMessageRef}
            className="flex scroll-mt-24 flex-col items-center justify-center gap-5 px-4 py-7.5"
          >
            <Graphics.checkCircle />
            <h3 className="font-cabinet-grotesk leading-h1 text-center text-2xl font-medium md:text-3xl">
              {__("We have received your message!", locale)}
            </h3>
            <p className="text-primary-light leading-h1 text-center">
              {__(
                "Thank you for contacting us. We have received your request and are working hard to respond as quickly as possible.",
                locale
              )}
            </p>
            <Link
              href={getHomePageSlug(locale)}
              className={cn(buttonVariants({ variant: "secondary", size: "md" }), "border-primary-light bg-inherit")}
            >
              {__("Return to home", locale)}
            </Link>
          </div>
          <ContactDetails data={data} locale={locale} className="block px-3.5 pb-7.5 md:hidden" />
        </>
      )}
    </div>
  );
};

export default ContactUsForm;
